import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import Icon from "@insite/mobius/Icon";
import {
    ImageModel,
    ProductModel,
} from "@insite/client-framework/Types/ApiModels";
import SwatchDesktop from "@ucs/Icons/SwatchDesktop";
import SwatchMobile from "@ucs/Icons/SwatchMobile";
import PlankDesktop from "@ucs/Icons/PlankDesktop";
import PlankMobile from "@ucs/Icons/PlankMobile";
import PaletteDesktop from "@ucs/Icons/PaletteDesktop";
import PaletteMobile from "@ucs/Icons/PaletteMobile";
import ZoomInDesktop from "@ucs/Icons/ZoomInDesktop";
import ZoomInMobile from "@ucs/Icons/ZoomInMobile";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import setSelectedImageIndex from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetSelectedImageIndex";
import { getSelectedImage } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@insite/content-library/Components/ProductPrimaryImage";
import { getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import ProductImageCarousel from "@ucs/Overrides/Components/ProductImageCarousel";

type Props = ImageSelectorProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;


interface ImageSelectorProps extends React.HTMLAttributes<HTMLElement> {
    css?: InjectableCss;
    product: ProductModel
}

const mapStateToProps = (state: ApplicationState, ownProps: ImageSelectorProps) => {
    return ({
        selectedModalImage: getSelectedImage(state, ownProps.product),  // TODO update this value
        selectedModalImageIndex: state.pages.productDetails.selectedImageIndex, // TODO update this value
    });
}

const mapDispatchToProps = {
    setSelectedImageIndex,
};

const ImageSelectorContainer = styled.div<InjectableCss>`
    position: relative;
    flex: 1;
    ${injectCss}
`;

const IconsContainer = styled.div`
    position: absolute;
    top: 0px;
    right: 0px;
    display: flex;
    flex-direction: column;
`;

const ImageContainer = styled('div')<{image: string}>`
    transition: opacity .3s linear;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    background-image: url('${p => p.image}');
    position: absolute;
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;
`;

const IconWrapper = styled('div')<{isMobile: boolean}>`
    background-color: rgba(0,0,0,.1);
    transition: background-color .3s linear;
    height: 70px;
    width: 70px;
    cursor: pointer;

    display: ${p => p.isMobile ? "none" : "inline-flex"};
    img {
        height: 70px;
        width: 70px;
    }

    ${p => 
        (breakpointMediaQueries(p.theme, 
        [css`
            display: ${p.isMobile ? "inline-flex" : "none"};
            height: 60px;
            width: 60px;
            img {
                height: 50px;
                width: 50px;
            }
             `]))};
`;

interface ImageProps extends React.HTMLAttributes<HTMLElement> {
    img: string;
    isActive: boolean;
}

interface IconProps extends React.HTMLAttributes<HTMLElement> {
    icon: React.NamedExoticComponent;
    isActive: boolean;
    isMobile?: boolean;
}

const SelectorImage : React.FC<ImageProps> = React.memo(props => {
    return <ImageContainer image={props.img} style={props.isActive ? {opacity: 1} : {opacity: 0}}>
        
    </ImageContainer>;
});

const SelectorIcon : React.FC<IconProps> = React.memo(props => {
    return <IconWrapper isMobile={props.isMobile || false} onClick={props.onClick} style={props.isActive ? {backgroundColor: "rgba(0,0,0,.35)"} : {}}>
        <Icon src={props.icon} size={props.isMobile? 60 : 70} />
    </IconWrapper>;
});

const fullPrimaryImageModal = {
    cssOverrides: {
        modalContainer: css`
            width: auto;
        `,
        modalBody: css`
            width: auto;
            max-width: initial;
            max-height: initial !important;
        `,
        modalTitle: css`
            border-bottom: none;
            padding: 10px 10px 0;
        `,
        modalContent: css`
            text-align: center;
        `,
    },
};

const fullPrimaryImage = {
    image: {
        css: css`
            img {
                width: auto;
                max-height: 70vh;
            }
        `,
    },
    img: {
        css: css`
            width: auto;
            max-height: 70vh;
        `,
    },
};

const ImageSelector : React.FC<Props> = ({
    product, 
    css, 
    selectedModalImage,
    selectedModalImageIndex,
    setSelectedImageIndex
    }:Props) => {
    const [selectedImage, setSelectedImage] = React.useState(0);
    const [fullPrimaryImageModalIsOpen, setFullImageModalIsOpen] = React.useState(false);
    const hasConstructed = React.useRef(false);

    const imageClickHandler = () => {
        setFullImageModalIsOpen(true);
    };
    const fullPrimaryImageModalCloseHandler = () => {
        setFullImageModalIsOpen(false);
    };

    const selectImageHandler = (index: number) => {
        setSelectedImageIndex({ index });
    };

    const image =
    selectedModalImage ??
    ({
        id: product.id,
        imageAltText: product.imageAltText,
        imageType: "Static",
        largeImagePath: product.largeImagePath,
        mediumImagePath: product.mediumImagePath,
        smallImagePath: product.smallImagePath,
    } as ImageModel);

    let imageSelectorImages = React.useRef<string[]>([]);
    let index = React.useRef<any>({});
    let indexSet = false;
    let imageSelectorIcons = React.useRef<React.NamedExoticComponent[]>([]);
    let imageSelectorIconsMobile = React.useRef<React.NamedExoticComponent[]>([]);

    const setImages = (images: ImageModel[]) =>{
        images.forEach((image: ImageModel, i) => {
            if (image.name.toLowerCase().includes("_swatch") || image.name.toLowerCase().includes("_alt1") || image.name.toLowerCase().includes("_alt2")) {
                index.current[image.largeImagePath] = i;
                if (image.name.toLowerCase().includes("_swatch")) {
                    setSelectedImageIndex({index: i});
                    indexSet = true;
                    imageSelectorImages.current.unshift(image.largeImagePath);
                    imageSelectorIcons.current.unshift(SwatchDesktop);
                    imageSelectorIconsMobile.current.unshift(SwatchMobile);
                } else if (image.name.toLowerCase().includes("_alt1")) {
                    if (!indexSet) {
                        setSelectedImageIndex({index: i});
                        indexSet = true;
                    }
                    imageSelectorImages.current.splice(1, 0, image.largeImagePath);
                    imageSelectorIcons.current.splice(1, 0, PlankDesktop);
                    imageSelectorIconsMobile.current.splice(1, 0, PlankMobile);
                } else if (image.name.toLowerCase().includes("_alt2")) {
                    if (!indexSet) {
                        setSelectedImageIndex({index: i});
                        indexSet = true;
                    }
                    imageSelectorImages.current.push(image.largeImagePath);
                    imageSelectorIcons.current.push(PaletteDesktop);
                    imageSelectorIconsMobile.current.push(PaletteMobile);
                }
            }
        });
    }

    if (!hasConstructed.current) {
        //Check for a swatch image
        if (product && product.images && product.images.length > 0) {
            setImages(product.images);
        }
        // imageSelectorIcons.current.push(ZoomInDesktop);
        // imageSelectorIconsMobile.current.push(ZoomInMobile);
        hasConstructed.current = true;
    }
  
    const onClick = (i: number) => {
        return () => {
            setSelectedImageIndex({index: index.current[imageSelectorImages.current[i]]});
            setSelectedImage(i);
            // if (i === (imageSelectorIcons.current.length - 1)) {
            //     imageClickHandler();
            // } else{
            //     setSelectedImageIndex({index: index.current[imageSelectorImages.current[i]]});
            //      setSelectedImage(i);
            // }
        }
    }

    return (
        <>
        <ImageSelectorContainer {...css}>
            {imageSelectorImages.current.map((img: string, i: number) => (
                <SelectorImage isActive={selectedImage === i} img={img} key={i}  />
            ))}
            {imageSelectorIcons.current.length > 1 && (
                <IconsContainer>
                    {imageSelectorIcons.current.map((icon: React.NamedExoticComponent, i: number) => (
                        <SelectorIcon isActive={selectedImage === i} icon={icon} key={i} onClick={onClick(i)}/>
                    ))}
                    {imageSelectorIconsMobile.current.map((icon: React.NamedExoticComponent, i: number) => (
                        <SelectorIcon isMobile={true} isActive={selectedImage === i} icon={icon} key={i} onClick={onClick(i)}/>
                    ))}
                </IconsContainer>
            )}
        </ImageSelectorContainer>
        <Modal
                {...fullPrimaryImageModal}
                isOpen={fullPrimaryImageModalIsOpen}
                handleClose={fullPrimaryImageModalCloseHandler}
            >
                <ProductPrimaryImage
                    product={product}
                    image={image}
                    useLargeImage={true}
                    extendedStyles={fullPrimaryImage}
                />
                {product.images && product.images.length > 1 && (
                    <ProductImageCarousel
                        selectedIndex={selectedModalImageIndex}
                        onSelectImage={selectImageHandler}
                    />
                )}
            </Modal>
        </>
    );
}

/** @component */
export default connect(mapStateToProps, mapDispatchToProps)(ImageSelector);